/**
 * Estado de servidor das tags.
 *
 * Mesma forma do `useLists`, e pelo mesmo motivo: tags mudam raramente e nao
 * tem acao desfazivel de alta frequencia. Escrita otimista aqui adicionaria
 * complexidade sem economizar espera perceptivel.
 *
 * A excecao e o EXCLUIR, que oferece desfazer (spec 10.3): apagar uma tag
 * remove o rotulo de todas as tarefas de uma vez, e essa e a unica acao daqui
 * cujo alcance vai alem do proprio registro.
 */

import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { uuidv7, type CreateTagPayload, type Tag, type UpdateTagPayload } from '@cleartask/shared';
import { api } from '../../lib/api.js';
import { useToast } from '../../components/ui/Toast.js';
import { taskKeys } from '../tasks/useTasks.js';

export const tagKeys = {
  all: ['tags'] as const,
};

export function useTags() {
  return useQuery({
    queryKey: tagKeys.all,
    queryFn: () => api.tags.list(),
    staleTime: 5 * 60_000,
  });
}

/**
 * Tags indexadas por id.
 *
 * A tarefa carrega `tagIds`, nao as tags inteiras — o card e o detalhe precisam
 * do nome e da cor. Fazer esse `find` dentro de cada card seria O(n·m) a cada
 * render da lista.
 */
export function useTagMap(): Map<string, Tag> {
  const { data } = useTags();
  return useMemo(() => new Map((data?.tags ?? []).map((tag) => [tag.id, tag])), [data?.tags]);
}

export function newTagId(): string {
  return uuidv7();
}

/**
 * Compara nomes de tag sem acento e sem caixa — "Proteção" casa com "protecao".
 *
 * O deslocamento de indice que o `NFD` provoca nao importa aqui (ao contrario
 * do parser em `shared/nlp.ts`, onde ele quebraria o recorte do titulo):
 * o resultado so e usado para comparar.
 */
export function normalizeTagName(valor: string): string {
  return valor
    .toLocaleLowerCase('pt-BR')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

export function useCreateTag() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateTagPayload) => api.tags.create(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tagKeys.all });
    },
  });
}

export function useUpdateTag() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateTagPayload }) =>
      api.tags.update(id, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tagKeys.all });
      // O nome e a cor aparecem nos chips do card: renomear muda o que a lista
      // de tarefas mostra sem que nenhuma tarefa tenha sido tocada.
      void qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

export function useDeleteTag() {
  const qc = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.tags.remove(id),

    onSuccess: ({ tag, affectedTasks }) => {
      void qc.invalidateQueries({ queryKey: tagKeys.all });
      void qc.invalidateQueries({ queryKey: taskKeys.all });

      showToast({
        // O numero entra na copy porque o alcance da acao nao esta na tela: a
        // pessoa apaga a tag na barra lateral e o efeito acontece em tarefas
        // que ela nao esta vendo.
        message:
          affectedTasks > 0
            ? `Tag excluída — removida de ${affectedTasks} tarefa(s)`
            : 'Tag excluída',
        action: {
          label: 'Desfazer',
          onAction: () => {
            void (async () => {
              // Recria com o MESMO id, a partir do registro que o DELETE
              // devolveu. As associacoes em `task_tags` foram embora com o
              // CASCADE e nao voltam — o desfazer restaura a TAG, nao as
              // marcacoes. Prometer mais seria mentir sobre o que aconteceu.
              await api.tags.create({ id: tag.id, name: tag.name, color: tag.color });
              void qc.invalidateQueries({ queryKey: tagKeys.all });
            })();
          },
        },
      });
    },
  });
}
